import { HttpErrorResponse } from '@angular/common/http';
import { MediaError } from './media-api';
import { formatSize } from './media-format';

/** The business error of vn: HTTP 570 `{message, data}` with a key `media.*`. Anything else (a network error, a 500) is not one. */
export function mediaErrorOf(err: unknown): MediaError | undefined {
  if (err instanceof HttpErrorResponse && err.error && typeof err.error === 'object') {
    const body = err.error as { message?: unknown; data?: unknown };
    if (typeof body.message === 'string') {
      const data = body.data && typeof body.data === 'object' ? (body.data as Record<string, unknown>) : undefined;
      return { message: body.message, data };
    }
  }
  return undefined;
}

/** The user may not write the photos (the server answers 403). */
export function isForbidden(err: unknown): boolean {
  return err instanceof HttpErrorResponse && err.status === 403;
}

export type Translate = (key: string, params?: Record<string, unknown>) => string;

/** The scope of the texts of the page: `productDefinitions` (the file `assets/i18n/product-definitions/*.json`), the block `media`. */
export const MEDIA_TEXTS = 'productDefinitions.media';

/** The keys of the business errors the page has a text for (`media.*` of `ProductMediaErrors`, and the ones the job writes into a FAILED item). */
export const MEDIA_ERROR_KEYS: readonly string[] = [
  'media.notConfigured',
  'media.size.notFound',
  'media.notFound',
  'media.upload.type',
  'media.upload.tooLarge',
  'media.upload.incomplete',
  'media.limit.photos',
  'media.limit.video',
  'media.replace.invalid',
  'media.notProbed',
  'media.frame.invalid',
  'media.order.mismatch',
  'media.copy.empty',
  'media.copy.same',
  'media.state',
  'media.retry.useless',
  'media.field.required',
  'media.storage',
  'media.process.timeout',
  'media.process.failed',
  'media.process.diskLimit',
  'media.file.type',
  'media.file.tooBig',
  'media.photo.tooSmall',
  'media.video.tooSmall',
  'media.video.tooLong'
];

/**
 * The errors of a file that a second try cannot mend (the file itself is wrong, or too big for the rules): «Повторить» is not offered for them, only «Удалить» and
 * «Заменить файл». The server refuses the retry of these keys too.
 */
export const PERMANENT_ERROR_KEYS: readonly string[] = [
  'media.photo.tooSmall',
  'media.video.tooSmall',
  'media.video.tooLong',
  'media.file.tooBig',
  'media.file.type',
  'media.upload.tooLarge'
];

/** Can «Повторить» help an item that failed with this error? (A key the page does not know, or none, can: the job may have failed on a bad moment.) */
export function canRetryError(key: string | null | undefined): boolean {
  return !key || !PERMANENT_ERROR_KEYS.includes(key);
}

const TYPE_WORDS: Record<string, string> = {
  'image/jpeg': 'JPG',
  'image/png': 'PNG',
  'image/webp': 'WebP',
  'image/heic': 'HEIC',
  'image/heif': 'HEIC',
  'video/mp4': 'MP4',
  'video/quicktime': 'MOV',
  'video/webm': 'WebM'
};

/** `image/heic,image/heif,image/jpeg` → `HEIC, JPG`: the types of the answer `media.upload.type` as words, each once. */
export function allowedTypesText(allowed: unknown): string {
  const list = typeof allowed === 'string' ? allowed.split(',') : Array.isArray(allowed) ? allowed.map(String) : [];
  const words = list.map(t => TYPE_WORDS[t.trim().toLowerCase()] ?? t.trim()).filter(Boolean);
  return [...new Set(words)].join(', ');
}

/** The data of an error made readable for a text: sizes in megabytes, lists joined. */
export function readableData(message: string, data: Record<string, unknown> | undefined, lang = 'ru'): Record<string, unknown> {
  const out: Record<string, unknown> = { ...data };
  if (message === 'media.upload.tooLarge' && typeof out['max'] === 'number') {
    out['max'] = formatSize(out['max'] as number, lang);
  }
  if (message === 'media.upload.type') {
    out['allowed'] = allowedTypesText(out['allowed']);
  }
  for (const [k, v] of Object.entries(out)) {
    if (Array.isArray(v)) {
      out[k] = v.join(', ');
    }
  }
  return out;
}

/** The part of the key after `media.`: `media.upload.tooLarge` → `upload.tooLarge`. */
export function errorTail(message: string): string {
  return message.replace(/^media\./, '');
}

/**
 * The text of an error by its key (`productDefinitions.media.errors.<tail>`); the code of a reason (`reason: 'tooSmall'`) and of a state (`status: 'READY'`)
 * has a word of its own. A key the page does not know gives the plain failure.
 */
export function mediaErrorText(translate: Translate, message: string, data?: Record<string, unknown>, lang = 'ru'): string {
  const tail = errorTail(message);
  const key = `${MEDIA_TEXTS}.errors.${tail}`;
  const params = readableData(message, data, lang);
  for (const [name, scope] of [['reason', 'reasons'], ['status', 'statuses']] as const) {
    const code = data?.[name];
    if (typeof code === 'string') {
      const wordKey = `${MEDIA_TEXTS}.errors.${scope}.${code}`;
      const word = translate(wordKey);
      params[`${name}Text`] = word === wordKey ? code : word;
    }
  }
  const text = translate(key, params);
  return text === key ? translate(`${MEDIA_TEXTS}.errors.failed`) : text;
}

/** A FAILED item keeps `error` as the key and, after a space, a short tail (a size, a tool's message): only the key is told to the editor, the tail is never shown. */
export function splitItemError(error: string | null | undefined): { key: string; tail: string } {
  const text = (error ?? '').trim();
  const space = text.indexOf(' ');
  return space < 0 ? { key: text, tail: '' } : { key: text.slice(0, space), tail: text.slice(space + 1).trim() };
}

/** The numbers the texts of the job's keys name: `{{min}}` is the smallest side of a photo or of a video, `{{seconds}}` the longest video. */
export function failureData(key: string, limits: { photoMinSide: number; videoMinSide: number; videoSeconds: number }): Record<string, unknown> {
  return { min: key === 'media.video.tooSmall' ? limits.videoMinSide : limits.photoMinSide, seconds: limits.videoSeconds };
}
